import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { isMobile } from 'react-device-detect';
import { useCashHandDetails } from '../../History/HistoryQueries';
import type {
  CashHandHistoryEntry,
  CashHandPlayer,
  CashHandShowdownCard,
  CashHandTurnHistoryEntry,
} from '../../History/Type/HistoryTypes';
import { COLORS, MONO_FONT, detailRowSx } from './HistoryConstants';
import {
  formatAmount,
  formatActionLabel,
  formatRoundLabel,
  formatDateTime,
  formatTimeOnly,
  formatGameTypeShort,
  getGameTypeColor,
  isSystemRound,
  actionHasAmount,
} from './HistoryFormatters';
import {
  CardDisplay,
  DetailIdDisplay,
  HiddenCardImg,
  DetailHeader,
  EmptyState,
  LoadingState,
  ErrorState,
  sectionBoxSx,
  sectionTitleSx,
} from './HistoryUI';

// ─── Props ───────────────────────────────────────────────────────────────────

interface CashHandDetailViewProps {
  userId: string;
  gameId: string;
  roomName?: string;
  startedAt?: string | number;
  summary?: CashHandHistoryEntry;
  onBack: () => void;
  active: boolean;
}

// ─── Component ───────────────────────────────────────────────────────────────

const CashHandDetailView: React.FC<CashHandDetailViewProps> = ({
  userId,
  gameId,
  roomName,
  startedAt,
  summary,
  onBack,
  active,
}) => {
  const detailsQuery = useCashHandDetails({
    userId,
    gameId,
    enabled: active && Boolean(gameId),
  });

  const details = detailsQuery.data;

  const { actionGroups } = useMemo(() => {
    const history = details?.turnHistory ?? [];
    const groups: Array<{ key: string; label: string; items: CashHandTurnHistoryEntry[] }> = [];
    let currentKey: string | null = null;
    let currentGroup: (typeof groups)[number] | null = null;
    let rake = 0;

    history.forEach((entry, index) => {
      // Extract rake amount for summary display
      if (entry.roundName === 'rake' && typeof entry.amount === 'number') {
        rake += entry.amount;
      }
      // Filter out system rounds (rake, prize, refund) from the action log
      if (isSystemRound(entry.roundName)) return;

      const key = entry.roundName ? entry.roundName.toString() : `round-${index}`;
      if (key !== currentKey) {
        currentKey = key;
        currentGroup = { key, label: formatRoundLabel(entry.roundName), items: [] };
        groups.push(currentGroup);
      }
      currentGroup?.items.push(entry);
    });
    return { actionGroups: groups, rakeAmount: rake || null };
  }, [details]);

  // ─── Derived values ────────────────────────────────────────────────────────

  const players = details?.players ?? [];
  const showdownCards = details?.showdownCards ?? [];
  const winners = details?.winners ?? [];
  const blinds = details?.blinds;
  const currentUserPlayer = players.find((player) => player.userId === userId);
  const currentUserShowdownCards = showdownCards.find((card) => card.userId === userId);
  const userHoleCards = details?.user?.holeCards?.length
    ? details.user.holeCards
    : currentUserPlayer?.holeCards?.length
      ? currentUserPlayer.holeCards
      : currentUserShowdownCards?.cards?.length
        ? currentUserShowdownCards.cards
        : summary?.user?.holeCards;
  const communityCards = details?.communityCards?.length
    ? details.communityCards
    : summary?.communityCards;

  const detailNetAmount = (() => {
    const direct = details?.user?.netAmount;
    if (typeof direct === 'number') return direct;
    const before = details?.user?.balanceBefore;
    const after = details?.user?.balanceAfter;
    if (typeof before === 'number' && typeof after === 'number') return after - before;
    return null;
  })();

  const anteText =
    blinds && typeof blinds.ante === 'number'
      ? formatAmount(blinds.ante, details?.currency)
      : null;

  const isAnteOnly = blinds && blinds.small === 0 && blinds.big === 0 && typeof blinds.ante === 'number' && blinds.ante > 0;

  const blindsText = isAnteOnly
    ? `Ante ${anteText}`
    : blinds && typeof blinds.small === 'number' && typeof blinds.big === 'number'
      ? `${formatAmount(blinds.small, details?.currency)}/${formatAmount(blinds.big, details?.currency)}${anteText ? ` \u2022 Ante ${anteText}` : ''}`
      : null;

  // ─── Grid columns ──────────────────────────────────────────────────────────

  const playerGridColumns = isMobile ? 'minmax(0, 1fr) 74px 78px' : '120px 1fr 100px 90px';
  const showdownGridColumns = isMobile ? '1fr auto' : '120px 1fr auto';

  // ─── Sub-renders ───────────────────────────────────────────────────────────

  const renderPlayer = (player: CashHandPlayer, index: number) => {
    const name = player.username || player.userId || `Player ${index + 1}`;
    const revealedShowdownCards = showdownCards.find((card) => (
      (player.userId && card.userId === player.userId) ||
      (player.username && card.username === player.username)
    ));
    const currentUserCards = player.userId === userId ? userHoleCards : undefined;
    const playerCards = player.holeCards?.length
      ? player.holeCards
      : currentUserCards?.length
        ? currentUserCards
        : revealedShowdownCards?.cards;
    const playerNet = typeof player.initialBalance === 'number' && typeof player.finalBalance === 'number'
      ? player.finalBalance - player.initialBalance
      : null;
    return (
      <Box
        key={`${player.userId}-${index}`}
        sx={{ ...detailRowSx(index), gridTemplateColumns: playerGridColumns }}
      >
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: COLORS.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</Typography>
        {!isMobile && (
          <Typography sx={{ fontSize: '13px', color: COLORS.textMuted, fontFamily: MONO_FONT }}>
            {formatAmount(player.initialBalance, details?.currency)} {'\u2192'} {formatAmount(player.finalBalance, details?.currency)}
          </Typography>
        )}
        <Typography sx={{
          fontSize: '13px',
          fontWeight: 600,
          fontFamily: MONO_FONT,
          textAlign: 'right',
          color: typeof playerNet === 'number' ? (playerNet > 0 ? COLORS.win : playerNet < 0 ? COLORS.loss : COLORS.textMuted) : COLORS.textMuted,
        }}>
          {typeof playerNet === 'number' ? `${playerNet > 0 ? '+' : ''}${formatAmount(playerNet, details?.currency)}` : '\u2014'}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
          {playerCards && playerCards.length > 0 ? (
            <CardDisplay cards={playerCards} />
          ) : (
            <>
              <HiddenCardImg />
              <HiddenCardImg />
            </>
          )}
        </Box>
      </Box>
    );
  };

  const renderShowdown = (card: CashHandShowdownCard, index: number) => {
    const name = card.username || card.userId || `Player ${index + 1}`;
    return (
      <Box
        key={`${card.userId}-${index}`}
        sx={{ ...detailRowSx(index), gridTemplateColumns: showdownGridColumns }}
      >
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: COLORS.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</Typography>
        {!isMobile && (
          <Typography sx={{ fontSize: '13px', color: COLORS.textMuted }}>{card.handRankName || 'Hand'}</Typography>
        )}
        <CardDisplay cards={card.cards} />
      </Box>
    );
  };

  // ─── Main render ───────────────────────────────────────────────────────────

  return (
    <Box>
      <DetailHeader
        title={details?.roomName || roomName || 'Cash Hand'}
        subtitle={formatDateTime(details?.startedAt || startedAt)}
        onBack={onBack}
        rightContent={
          <Stack direction="row" spacing={1} alignItems="center">
            {details?.gameType && (
              <Box component="span" sx={{ fontSize: '12px', fontWeight: 700, fontFamily: 'Inter, sans-serif', color: getGameTypeColor(details.gameType).color, bgcolor: getGameTypeColor(details.gameType).bg, borderRadius: '4px', px: 1, py: 0.25, lineHeight: 1.6 }}>
                {formatGameTypeShort(details.gameType)}
              </Box>
            )}
          </Stack>
        }
      />

      {detailsQuery.isLoading && <LoadingState />}
      {detailsQuery.error && <ErrorState />}
      {!detailsQuery.isLoading && !detailsQuery.error && !details && (
        <EmptyState message="Hand details not available." />
      )}
      {!detailsQuery.isLoading && !detailsQuery.error && details && (
        <Stack spacing={0.75}>
          {/* Hand summary + cards */}
          <Box sx={sectionBoxSx}>
            <Box sx={{ mb: 0.75 }}>
              <DetailIdDisplay label="Game ID" id={details.gameId || gameId} copyLabel="game" />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: isMobile ? 1 : 2, rowGap: 0.75 }}>
              <SummaryItem label={isAnteOnly ? 'Stakes' : 'Blinds'} value={blindsText || '\u2014'} />
              <SummaryItem label="Pot" value={formatAmount(details.pot, details.currency)} />
              {/* {typeof rakeAmount === 'number' && <SummaryItem label="Rake" value={formatAmount(rakeAmount, details.currency)} />} */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>P/L</Typography>
                <Typography sx={{
                  fontSize: '13px',
                  fontWeight: 700,
                  fontFamily: MONO_FONT,
                  color: typeof detailNetAmount === 'number' ? (detailNetAmount > 0 ? COLORS.win : detailNetAmount < 0 ? COLORS.loss : COLORS.textSecondary) : COLORS.textMuted,
                }}>
                  {typeof detailNetAmount === 'number' ? `${detailNetAmount > 0 ? '+' : ''}${formatAmount(detailNetAmount, details.currency)}` : '\u2014'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mt: 0.75 }}>
              <CardDisplay cards={userHoleCards} label="Hole" />
              <CardDisplay cards={communityCards} label="Board" />
            </Box>
          </Box>

          {/* Players */}
          <Box sx={sectionBoxSx}>
            <Typography sx={sectionTitleSx}>Players</Typography>
            {players.length === 0 ? (
              <Typography sx={{ fontSize: '13px', color: COLORS.textMuted }}>No player details available.</Typography>
            ) : (
              <Box>
                {/* Column headers */}
                <Box sx={{ display: 'grid', gridTemplateColumns: playerGridColumns, gap: 1, px: 1, pb: 0.5, borderBottom: `1px solid ${COLORS.borderLight}` }}>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Player</Typography>
                  {!isMobile && (
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Stack</Typography>
                  )}
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'right' }}>P/L</Typography>
                  {!isMobile && (
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'right' }}>Cards</Typography>
                  )}
                  {isMobile && (
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'right' }}>Cards</Typography>
                  )}
                </Box>
                {players.map(renderPlayer)}
              </Box>
            )}
          </Box>

          {/* Showdown */}
          {showdownCards.length > 0 && (
            <Box sx={sectionBoxSx}>
              <Typography sx={sectionTitleSx}>Showdown</Typography>
              <Box>{showdownCards.map(renderShowdown)}</Box>
            </Box>
          )}

          {/* Action Log */}
          <Box sx={sectionBoxSx}>
            <Typography sx={sectionTitleSx}>Action Log</Typography>
            {actionGroups.length === 0 ? (
              <Typography sx={{ fontSize: '12px', color: COLORS.textDisabled }}>No action history available.</Typography>
            ) : (
              <Stack spacing={0.75}>
                {actionGroups.map((group) => (
                  <Box key={group.key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                      <Typography sx={{ fontSize: '11px', fontWeight: 700, color: COLORS.primary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {group.label}
                      </Typography>
                      <Box sx={{ flex: 1, height: '1px', background: COLORS.gradientDivider }} />
                    </Box>
                    <Box>
                      {group.items.map((entry, index) => (
                        <Box
                          key={`${group.key}-${index}`}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 0.75,
                            py: 0.35,
                            borderRadius: '3px',
                            bgcolor: index % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.015)',
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.04)' },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: COLORS.textPrimary, minWidth: '70px' }}>
                              {entry.username || entry.playerId || 'Player'}
                            </Typography>
                            <Typography sx={{ fontSize: '13px', color: COLORS.textMuted }}>
                              {formatActionLabel(entry.action)}{actionHasAmount(entry.action) && typeof entry.amount === 'number' ? ` ${formatAmount(entry.amount, details.currency)}` : ''}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: '12px', color: COLORS.textMuted, ml: 1, fontFamily: MONO_FONT }}>
                            {formatTimeOnly(entry.time)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          {/* Winners */}
          {winners.length > 0 && (
            <Box sx={{ ...sectionBoxSx, borderColor: COLORS.winBorder }}>
              <Typography sx={sectionTitleSx}>Winners</Typography>
              <Box>
                {winners.map((winner, index) => (
                  <Box
                    key={`${winner.userId}-${index}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 1,
                      py: 0.35,
                      borderRadius: '3px',
                      bgcolor: index % 2 === 0 ? COLORS.winBg : 'rgba(76, 175, 80, 0.06)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: COLORS.textPrimary }}>
                        {winner.username || winner.userId || `Winner ${index + 1}`}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: COLORS.textMuted }}>
                        {winner.handRankName || ''}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: COLORS.win, fontFamily: MONO_FONT }}>
                      +{formatAmount(winner.amount, details.currency)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
};

const SummaryItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</Typography>
    <Typography sx={{ fontSize: '13px', color: COLORS.textSecondary, fontFamily: MONO_FONT }}>{value}</Typography>
  </Box>
);

export default CashHandDetailView;
